/*global Autodesk*/
import { NodeTypes } from "@/types/LCAoM/TreeBuilder";
import { ModelTreeNode } from "@/types/ModelTreeNode";
import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD } from "@components/DataGrid/components/colDef/GridVisibilityColDef";
import CustomFooterRowCount from "@components/DataGrid/components/footer/CustomFooterRowCount";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import "@components/DataGrid/models/dataGridProps";
import { GridRowVisibilitySelectionPropagation } from "@components/DataGrid/models/gridRowVisibilitySelectionModel";
import useModel from "@features/analysis/hooks/useModel";
import useMultiplier, { parseMultiplier } from "@features/analysis/utils/multiplier";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import { Tooltip, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellEditStartParams,
  GridCellEditStopParams,
  GridFilterItem,
  GridFilterModel,
  GridGroupNode,
  GridRow,
  GridRowId,
  GridRowModel,
  GridRowProps,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { gridRowBackgroundColor } from "@utils/muiHelpers";
import { forwardRef, MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import useLmViewerSync from "../../hooks/useLmViewerSync";
import { getColumns, getFilteredColumns, getGroupingColumnDef } from "./MuiModelGrid.columns";
import { numberComparatorAgg, sumWithNullsAgg, textComparatorAgg, truthyAgg } from "@components/DataGrid/utils/aggregation";
// Extended data fields that are editable
const EXTENDED_FIELDS = ["systemId", "subSystemId", "attribute1", "attribute2", "attribute3"] as const;

export interface AssignGridProps {
  apiRef: MutableRefObject<GridApiPremium>;
  modelId: string | undefined;
  dataVersion?: number;
  onRowModified?: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const gridStorageKey = "model-grid";

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  aggregation: {
    model: {
      siblings: "sum",
      quantityTotal: "sum",
      lengthTotal: "sum",
      areaTotal: "sum",
      volumeTotal: "sum",
      thickness: "sum",
      assigned: "truthy",
      cadModelCategory: "textComp",
      cadFamily: "textComp",
      type: "textComp",
    },
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
  },
  rowVisibilitySelection: [],
};

const aggregationFunctions = {
  ...GRID_AGGREGATION_FUNCTIONS,
  truthy: truthyAgg,
  sum: sumWithNullsAgg,
    textComp: textComparatorAgg,
  numberComp: numberComparatorAgg,
};

const getSystemTypeValues = (row: any) => {
  const parameters = row?.data?.modelParameters || [];
  const systemTypeParams = parameters.filter((p: any) => typeof p?.name === "string" && /system\s*type/i.test(p.name));
  return systemTypeParams
    .map((p: any) => p?.value)
    .filter((value: any) => value !== undefined && value !== null && String(value).trim() !== "");
};

const SystemTypeGridRow = forwardRef<HTMLDivElement, GridRowProps>((props, ref) => {
  const systemTypeValues = getSystemTypeValues(props.row);

  if (systemTypeValues.length === 0) {
    return <GridRow {...props} ref={ref} />;
  }

  const rowName = props.row?.data?.cadName || props.row?.label || "Element";
  const tooltipTitle = (
    <div style={{ maxWidth: "400px", color: "#FFFFFF" }}>
      <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#FFFFFF" }}>
        {i18n.t("model-grid.columns.system-type")} for {rowName}
      </div>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#FFFFFF", fontFamily: "inherit" }}>
        {systemTypeValues.map((value: string) => `  ${value}`).join("\n")}
      </pre>
    </div>
  );

  return (
    <Tooltip
      title={tooltipTitle}
      placement="bottom-start"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(97, 97, 97, 0.95)",
            color: "#FFFFFF",
            fontSize: "12px",
            padding: "8px 12px",
            maxWidth: "400px",
          },
        },
        arrow: {
          sx: {
            color: "rgba(97, 97, 97, 0.95)",
          },
        },
      }}
    >
      <GridRow {...props} ref={ref} />
    </Tooltip>
  );
});

SystemTypeGridRow.displayName = "SystemTypeGridRow";

const slots = {
  toolbar: GridToolbarCustom,
  footerRowCount: CustomFooterRowCount,
  row: SystemTypeGridRow,
};

const slotProps = {
  toolbar: {
    showQuickFilter: false,
    showSearchFilter: true,
    searchFilterProps: {
      collapsible: true,
    },
  },
};

const getRowId = (row: any) => {
  try {
    return row.key;
  } catch (error) {
    console.error("getRowId error: ", error);
  }
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

// TODO: this is causing a bug in the grid,
// when we update the Category rows with the Element rows
// there seems to be an async method running that then
// tries to access rows based on rowId and the rows are empty
// so it throws a row not found error
const getRowClassName = (params: any) => {
  if (params.row.type === "category") {
    return "mui-treetable-category";
  }
  if (params.row.type === "group") {
    if (params.row.data?.nodeType === NodeTypes.Family) {
      return "mui-treetable-family";
    }
    return "mui-treetable-group";
  }
  if (params.row.type === "element" && params.row.leaf) {
    return "mui-treetable-element";
  }
  // TODO: this looks SUS. Why is type of element being assigned a group class?
  if (params.row.type === "element" && !params.row.leaf) {
    return "mui-treetable-group";
  }
  if (params.row.type === "component") {
    return "mui-treetable-component";
  }
  return "";
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const rowVisibilitySelectionPropagation: GridRowVisibilitySelectionPropagation = {
  parents: true,
  descendants: true,
};

const MuiModelGridRef = forwardRef<any, PropsWithChildren<AssignGridProps>>((props, ref) => {
  const { apiRef, modelId: inputModelId, dataVersion, onRowModified } = props;
  const theme = useTheme();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const [key, setKey] = useState(0); // increment key to force re-initialize of the grid
  const [modelId, setModelId] = useState<string | undefined>(inputModelId);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [gridHeight, setGridHeight] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const filterModelRef = useRef<GridFilterModel>({ items: [] });
  const containerRef = useRef<any>(null);
  const appSettings = useAppSettings();
  const cadModelCategories = useCadModelCategories();
  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("assign-grid.columns.model")), [apiRef]);
  const { loading, co2MaterialTypeSpecKind } = useModel({ apiRef, modelId, dataVersion });
  const {
    updateMultiplierInChildren,
    updateNotesInChildren,
    getAncestorRows,
    updateSystemInChildren,
    updateSubSystemInChildren,
    updateAttributeInChildren,
    updateCarbonImpactInChildren,
    updateCarbonImpactNotesInChildren,
  } = useMultiplier();
  const [editingCellId, setEditingCellId] = useState<GridRowId | null>(null);
  useLmViewerSync({ apiRef, lmViewer: appStore.lmViewer1 });
  filterModelRef.current = filterModel;

  const scopeMap = useMemo(() => {
    return appSettings.data?.modelScopeSetting?.modelScopeSettingsItems?.reduce((acc: any, item: any) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [appSettings.data]);

  const columns = useMemo(() => getColumns(apiRef, scopeMap, appStore, theme, co2MaterialTypeSpecKind), [apiRef, scopeMap, appStore, theme, co2MaterialTypeSpecKind]);
  const { filteredColumns, columnVisibilityModel: initialVisibilityModel } = useMemo(() => getFilteredColumns(columns, appStore), [columns, appStore]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(initialVisibilityModel);

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      columns: {
        columnVisibilityModel,
      },
      headerFilters: true,
    }),
    [columnVisibilityModel],
  );

  useEffect(() => {
    if (inputModelId !== modelId) {
      // Clear cached data for the new model to force fresh loading
      if (inputModelId) {
        // Remove all queries for this model (including those with dataVersion)
        appStore.queryClient.removeQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              (queryKey[0] === "projectModel" && queryKey[1] === inputModelId) ||
              //(queryKey[0] === "categories" && queryKey[1] === inputModelId) ||
              //(queryKey[0] === "elements" && queryKey[1] === inputModelId) ||
              //(queryKey[0] === "components" && queryKey[1] === inputModelId) ||
              (queryKey[0] === "modelNodes" && queryKey[1] === inputModelId) ||
              (queryKey[0] === "materials" && queryKey[1] === inputModelId)
            );
          },
        });
      }

      setKey(key + 1);
      setModelId(inputModelId);
    }
  }, [inputModelId, modelId]);

  // Handle case where model becomes null (e.g., after deletion)
  useEffect(() => {
    if (!inputModelId && modelId) {
      setKey(key + 1);
      setModelId(undefined);
    }
  }, [inputModelId, modelId]);

  // Initialize lookups in AppStore when data changes
  useEffect(() => {
    appStore.initCadModelCategoryLookup(cadModelCategories.data);
  }, [cadModelCategories.data, appStore]);

  const updateGridHeight = (containerHeight: number) => {
    const padding = 8;
    const newGridHeight = `${containerHeight - padding}px`;
    setGridHeight(newGridHeight);
  };

  useResizeObserver({
    ref: containerRef,
    onResize: ({ height }: { height: number | undefined }) => {
      if (height && height !== containerHeight) {
        setContainerHeight(height);
        updateGridHeight(height);
      }
    },
  });

  const sx = useMemo(() => {
    return {
      height: gridHeight,
      maxHeight: gridHeight,
      // Alter the row background color for elements and components
      "& .MuiDataGrid-row.mui-treetable-element:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
      "& .MuiDataGrid-row.mui-treetable-component:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
      "& .MuiDataGrid-row.mui-treetable-family:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
    };
  }, [gridHeight, appStore.colorMode]);

  const getAggregatePosition = useCallback((groupNode: GridGroupNode) => {
    return groupNode.depth === -1 ? "footer" : "inline";
  }, []);

  const handleRowSelectionChange = useCallback(
    (selection: any) => {
      console.log("Selection Changed:", selection);

      // MuiAssignTab is expecting an object with keys & value
      // to enable/disable the assign buttons
      // however, we no longer need to maintain a store of all the selected items
      // since we can just query the MUI DataGrid
      // so for now we'll just create an object with one key
      // which will trigger the button to be enabled/disabled
      var selectionCount = selection.length === 0 ? {} : { count: selection.length };
      appStore.setSelectedNodesKeys(selectionCount);

      const selectedRows = apiRef.current.getSelectedRows();
      console.log("Selected Rows: ", selectedRows);

      const cadIds: string[] = selectedRows.values().reduce((acc: any, row: any) => {
        if (row.data.cadId) {
          acc.push(row.data.cadId);
        }
        return acc;
      }, []);
      console.log("cadIds: ", cadIds);

      const urns = selectionManager.getSelectedModelUrns(selectionManager.selectedScenarioId!);
      appStore.lmViewer1?.selectByCadIds(cadIds, urns[0]);
    },
    [apiRef, appStore, selectionManager],
  );

  const handleCellSelectionModelChange = useCallback((selection: any) => {
    console.log("Cell Selection Changed:", selection);

    const ignoreCell = Object.values(selection).find((cells: any) => {
      return Object.entries(cells).find(([field, _value]: [string, any]) => field === GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD);
    });
    if (ignoreCell) {
      return;
    }

    apiRef.current.setRowVisibilityModel([]);
    Object.entries(selection).forEach(([rowId, cells]: [string, any]) => {
      Object.entries(cells).forEach(([field, _value]: [string, any]) => {
        if (field === GRID_TREE_DATA_GROUPING_FIELD || field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD) {
          const isRowVisibilitySelected = apiRef.current.isRowVisibilitySelected(rowId);
          apiRef.current.setRowVisibility(rowId, !isRowVisibilitySelected);
        }
      });
    });
  }, []);

  const handleRowVisibilitySelectionChange = useCallback(
    (selection: any) => {
      console.log("Visibility Selection Changed:", selection);

      const visibleRows = apiRef.current.getVisibleRows();
      console.log("Visible Rows: ", visibleRows);

      const cadIds: string[] = visibleRows.values().reduce((acc: any, row: any) => {
        if (row.data.cadId) {
          acc.push(row.data.cadId);
        }
        return acc;
      }, []);
      console.log("cadIds: ", cadIds);

      const urns = selectionManager.getSelectedModelUrns(selectionManager.selectedScenarioId!);
      appStore.lmViewer1?.isolateByCadIds(cadIds, urns[0]);
    },
    [selectionManager],
  );

  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      console.log("Filter Model Changed:", newFilterModel);

      if (!newFilterModel.items.find((item: GridFilterItem) => item.field === "cadId")) {
        if (filterModelRef.current.items.find((item: GridFilterItem) => item.field === "cadId")) {
          appStore.lmViewer1?.turnOnAllVisibility();
          // TODO: we need to turn on visibility for all rows
        }
      }

      setFilterModel(newFilterModel);
    },
    [appStore.lmViewer1],
  );

  const processRowUpdate = useCallback(async (updatedRow: GridRowModel, _originalRow: GridRowModel) => {
    console.log("processRowUpdate: ", updatedRow);
    const input = updatedRow.data?.multiplierInput ?? "";
    const parsed = parseMultiplier(input);
    const isInvalid = input.trim() !== "" && parsed === null;

    const carbonInput = updatedRow.data?.carbonImpactInput ?? "";
    const carbonParsed = parseMultiplier(carbonInput);
    const isCarbonInvalid = carbonInput.trim() !== "" && carbonParsed === null;

    return {
      ...updatedRow,
      data: {
        ...updatedRow.data,
        multiplierInput: input,
        multiplierValue: parsed,
        // Preserve new fields from the updated row
        systemId: updatedRow.data?.systemId ?? "",
        subSystemId: updatedRow.data?.subSystemId ?? "",
        attribute1: updatedRow.data?.attribute1 ?? "",
        attribute2: updatedRow.data?.attribute2 ?? "",
        attribute3: updatedRow.data?.attribute3 ?? "",
        notes: updatedRow.data?.notes ?? "",
        // CARBON fields preserved and set
        carbonImpactInput: carbonInput,
        carbonImpactValue: carbonParsed,
        carbonImpactNotes: updatedRow.data?.carbonImpactNotes ?? "",
        hasChildCarbonImpactOverride: updatedRow.data?.hasChildCarbonImpactOverride ?? false,

        error: isInvalid ? i18n.t("scope-tab.multiplier.validation-message") : undefined,

        carbonError: isCarbonInvalid ? i18n.t("scope-tab.multiplier.validation-message") : undefined,
      },
    };
    // TODO: Rebar and Non-Modelled edit validations
  }, []);

  const processRowUpdateError = useCallback(async () => {
    // TODO: Rebar and Non-Modelled edit validation errors
  }, []);

  const refreshAncestors = (apiRef: MutableRefObject<GridApiPremium>, row: any) => {
    const ancestors = getAncestorRows(apiRef, row);
    if (ancestors.length > 0) {
      apiRef.current.updateRows(ancestors);
      // also recurse upward (refresh grandparents too)
      ancestors.forEach((ancestor) => refreshAncestors(apiRef, ancestor));
    }
  };

  const handleCellEditStart = (params: GridCellEditStartParams) => {
    if (params.field === "multiplierValue") {
      setEditingCellId(params.id);

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          multiplierValue: params?.row?.data?.multiplierInput,
        },
      };

      // Update current row
      apiRef.current.updateRows([{ id: params.id, ...updatedRow }]);
    }
    if (params.field === "notes") {
      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          notes: params?.row?.data?.notes,
        },
      };

      // Update current row
      apiRef.current.updateRows([{ id: params.id, ...updatedRow }]);
    }

    if (params.field === "carbonImpactValue") {
      setEditingCellId(params.id);

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          carbonImpactValue: params?.row?.data?.carbonImpactInput,
        },
      };

      // Update current row
      apiRef.current.updateRows([{ id: params.id, ...updatedRow }]);
    }

    if (params.field === "carbonImpactNotes") {
      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          notes: params?.row?.data?.carbonImpactNotes,
        },
      };

      // Update current row
      apiRef.current.updateRows([{ id: params.id, ...updatedRow }]);
    }

    if (EXTENDED_FIELDS.includes(params.field as any)) {
      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          [params.field]: params?.row?.data?.[params.field] || "",
        },
      };

      // Update current row
      apiRef.current.updateRows([{ id: params.id, ...updatedRow }]);
    }
  };

  const handleCellEditStop = (params: GridCellEditStopParams) => {
    if (params.field == "multiplierValue") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value;

      // const newValue = params?.row?.data?.multiplierInput?.trim();

      if (!newValue) {
        setEditingCellId(null);
      }

      const parsed = parseMultiplier(newValue);
      if (parsed === null) {
        setEditingCellId(null);
      }

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          multiplierInput: newValue,
          multiplierValue: parsed,
        },
        multiplierInput: newValue,
        multiplierValue: parsed,
      };

      updateMultiplierInChildren(apiRef, updatedRow, newValue);
      refreshAncestors(apiRef, updatedRow);

      // Track modified row
      if (onRowModified) {
        onRowModified((prev) => new Set(prev).add(params.id.toString()));
      }
    }

    if (params.field == "notes") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value ?? "";

      const editedId = params.id;

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        id: editedId,
        key: editedId,
        data: {
          ...params?.row?.data,
          notes: newValue,
        },
      };
      apiRef.current.updateRows([updatedRow]);
      updateNotesInChildren(apiRef, updatedRow, newValue);

      // Track modified row
      if (onRowModified) {
        onRowModified((prev) => new Set(prev).add(editedId.toString()));
      }
    }

    if (params.field === "carbonImpactValue") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value;

      if (!newValue) {
        setEditingCellId(null);
      }

      const parsed = parseMultiplier(newValue);
      // if invalid, just clear editing state (grid will show validation via processRowUpdate)
      if (parsed === null) {
        setEditingCellId(null);
      }

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        data: {
          ...params?.row?.data,
          carbonImpactInput: newValue,
          carbonImpactValue: parsed,
        },
        carbonImpactInput: newValue,
        carbonImpactValue: parsed,
      };

      // update children carbon impact fields (pattern similar to multiplier but WITHOUT metric changes)
      updateCarbonImpactInChildren(apiRef, updatedRow, newValue);
      refreshAncestors(apiRef, updatedRow);

      // Track modified row
      if (onRowModified) {
        onRowModified((prev) => new Set(prev).add(params.id.toString()));
      }
    }

    if (params.field == "carbonImpactNotes") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value ?? "";

      const editedId = params.id;

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        id: editedId,
        key: editedId,
        data: {
          ...params?.row?.data,
          carbonImpactNotes: newValue,
        },
      };
      apiRef.current.updateRows([updatedRow]);
      // update carbon impact notes in children (separate helper)
      updateCarbonImpactNotesInChildren(apiRef, updatedRow, newValue);

      // Track modified row
      if (onRowModified) {
        onRowModified((prev) => new Set(prev).add(editedId.toString()));
      }
    }

    if (EXTENDED_FIELDS.includes(params.field as any)) {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value ?? "";

      const editedId = params.id;

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        id: editedId,
        key: editedId,
        data: {
          ...params?.row?.data,
          [params.field]: newValue,
        },
      };
      apiRef.current.updateRows([updatedRow]);

      // Track modified row
      if (onRowModified) {
        onRowModified((prev) => new Set(prev).add(editedId.toString()));
      }
    }

    if (params.field === "systemId") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value ?? "";

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        id: params.id,
        key: params.id,
        data: {
          ...params?.row?.data,
          systemId: newValue,
          subSystemId: "", // reset SubSystem
        },
      };

      apiRef.current.updateRows([updatedRow]);
      updateSystemInChildren(apiRef, updatedRow, newValue);
      updateSubSystemInChildren(apiRef, updatedRow, ""); // clear children subs
    }

    if (params.field === "subSystemId") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value ?? "";

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        id: params.id,
        key: params.id,
        data: {
          ...params?.row?.data,
          subSystemId: newValue,
        },
      };

      apiRef.current.updateRows([updatedRow]);
      updateSubSystemInChildren(apiRef, updatedRow, newValue);
    }

    if (params.field === "attribute1" || params.field === "attribute2" || params.field === "attribute3") {
      const editState = apiRef.current.state.editRows[params.id];
      const newValue = editState?.[params.field]?.value ?? "";

      const updatedRow: ModelTreeNode = {
        ...params?.row,
        id: params.id,
        key: params.id,
        data: {
          ...params?.row?.data,
          [params.field]: newValue,
        },
      };

      apiRef.current.updateRows([updatedRow]);
      updateAttributeInChildren(apiRef, updatedRow, newValue, params.field as any);
    }

    setEditingCellId(null);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        width: "100%",
      }}
    >
      {gridHeight && (
        <DataGrid
          key={key}
          ref={ref}
          apiRef={apiRef}
          treeData
          autosizeColumns
          autosizeTreeDataGroupingColumns
          checkboxSelection
          disableRowSelectionOnClick
          getTreeDataPath={getTreeDataPath}
          columns={filteredColumns}
          // NOTE: these prevents column dimensions from being restored https://github.com/mui/mui-x/issues/17681#issuecomment-3012528602
          // and with the useSaveGridState hook, the column dimensions are restored from localStorage
          //columnVisibilityModel={columnVisibilityModel}
          //onColumnVisibilityModelChange={handleColumnVisibilityChange}
          rowSelectionPropagation={rowSelectionPropagation}
          initialState={initialState}
          groupingColDef={groupingColumnDef}
          getRowId={getRowId}
          aggregationFunctions={aggregationFunctions}
          getAggregationPosition={getAggregatePosition}
          getRowClassName={getRowClassName}
          onRowSelectionModelChange={handleRowSelectionChange}
          onRowVisibilitySelectionModelChange={handleRowVisibilitySelectionChange}
          slots={slots}
          slotProps={slotProps}
          sx={sx}
          loading={loading}
          throttleRowsMs={0}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={processRowUpdateError}
          rowVisibilitySelection
          rowVisibilitySelectionPropagation={rowVisibilitySelectionPropagation}
          cellSelection
          onCellSelectionModelChange={handleCellSelectionModelChange}
          filterModel={filterModelRef.current}
          onFilterModelChange={handleFilterModelChange}
          rowExpander
          storageKey={gridStorageKey}
          onCellEditStart={handleCellEditStart}
          onCellEditStop={handleCellEditStop}
        />
      )}
    </Box>
  );
});

const MuiModelGrid = MuiModelGridRef;
export default MuiModelGrid;
